import React, { PropsWithChildren, useEffect, useState } from 'react';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { Grid, Typography } from '@mui/material';
import { slugify } from 'helpers/slugify';
import { useQuery } from 'hooks/useQuery';
import { useDrawerClicked } from 'state/contexts/drawer';
import {
  StyledAccordion,
  StyledAccordionCounter,
  StyledAccordionDetails,
  StyledAccordionSubtitle,
  StyledAccordionSummary,
} from './styles';

type AccordionProps = PropsWithChildren;
export interface IAccordion extends AccordionProps {
  id?: string;
  title: string;
  onChange?: (name: string) => (event: React.SyntheticEvent, expanded: boolean) => void;
  name?: string;
  expanded?: boolean;
  alwaysExpanded?: boolean;
  hideExpandIcon?: boolean;
  counter?: string;
  subtitle?: string;
  buttons?: any;
}

const Accordion = ({
  id,
  children,
  title,
  onChange,
  name = slugify(title),
  expanded = false,
  alwaysExpanded = false,
  hideExpandIcon = false,
  counter,
  subtitle,
  buttons,
}: IAccordion) => {
  const [queryExpanded, setQueryExpanded] = useState<boolean>(false);
  const { drawerClicked, setDrawerClicked } = useDrawerClicked();

  const query = useQuery();
  const currentExpandQueryParamValue: string | null = query.get('expand');

  const nameIsEqualToParamValue = () =>
    !!name && !!currentExpandQueryParamValue && name === currentExpandQueryParamValue;

  useEffect(() => {
    if (nameIsEqualToParamValue()) {
      expanded = true;
    }
  }, [name]);

  useEffect(() => {
    if (expanded || alwaysExpanded) {
      setQueryExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  useEffect(() => {
    setQueryExpanded(expanded || alwaysExpanded);
  }, [expanded]);

  useEffect(() => {
    if (drawerClicked && !nameIsEqualToParamValue() && !alwaysExpanded) {
      setQueryExpanded(false);
      setDrawerClicked(false);
    }
  }, [drawerClicked, name]);

  const handleChange = () => {
    setQueryExpanded((prevState) => !prevState || alwaysExpanded);
    if (onChange) {
      onChange(name);
    }
  };

  return (
    <StyledAccordion expanded={queryExpanded} onChange={handleChange}>
      <StyledAccordionSummary
        expandIcon={
          !alwaysExpanded && !hideExpandIcon ? (
            <Button variant={EButtonVariants.outlined} icon={EIconTypes.dropdownDown} />
          ) : (
            <span />
          )
        }
        alwaysExpanded={alwaysExpanded}
        id={id || slugify(title)}
      >
        <Grid
          container
          spacing={1}
          sx={{
            width: '100%',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">{title}</Typography>
          {counter && <StyledAccordionCounter>{counter}</StyledAccordionCounter>}
          {subtitle && (
            <StyledAccordionSubtitle
              expanded={queryExpanded}
              variant="subtitle1"
              className="accordion-subtitle"
            >
              {subtitle}
            </StyledAccordionSubtitle>
          )}
          {buttons}
        </Grid>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

export { Accordion };
